import { Box } from "@mui/material"
import { useSearchParams } from "react-router-dom"
import { Bound } from "lib/@components/binding/Bound"
import { useReference } from "lib/@hooks/use-reference"
import { clean } from "lib/clean"
import { navigate } from "lib/routes/navigate"
import { ScheduleSelectorComponent } from "routes/schedule/components/schedule-selector-component"
import { useTreeItem } from "routes/schedule/lib/useTreeItem"
import { handleUnlicensedContent } from "routes/schedule/lib/handle-unlicensed-content"
import {
    getFacilityProfileNavigatorRoute,
    getFacilityProfileScheduleRoute,
} from "routes/facilities/plugins/maintenance-profile-tab/facility-profile-routes"
import { ShareCommentsTabRegistrar } from "routes/schedule/plugins/share-link-comments/share-comments-tab-registrar"
import { RequestedInformationTabRegistrar } from "routes/sharing/share-link-fields/requested-information-tab-registrar"
// Imported for its module-level ScheduleSort/ScheduleFilter toolbar plugs, which are
// otherwise only registered once the in-app schedule library page has been visited
import "routes/schedule/components/schedule-selector"

// Share links browse a regime through the same selector interface used when viewing a
// regime inside the app (stats panel, filter/sort toolbar, decorated rows), instead of
// the grid of generated-colour tiles. The hosting page already supplies the title and
// back button, so no breadcrumb header is rendered here. Drill-down stays on this page
// via the ?id= param. isShare suppresses the publishing-stage steps for share visitors.
export function ShareRegimeSelector({ regimeId }) {
    const [searchParams, setSearchParams] = useSearchParams()
    const share = searchParams.get("share")
    const parentId = new URLSearchParams(window.location.search).get("id") ?? regimeId
    useReference({}, "search", "sortMode")
    const parentItem = useTreeItem(parentId, true)

    return (
        <Bound
            parentId={parentId}
            parentItem={parentItem}
            isShare={true}
            onClickSchedule={open}
            scheduleProfileRoute={scheduleProfileRoute}
            regimeNavigatorRoute={regimeNavigatorRoute}
        >
            <Box
                display="flex"
                flexDirection="column"
                height={1}
                sx={{ overflowY: "auto", overflowX: "visible" }}
                flex={1}
                mx={2}
            >
                <ScheduleSelectorComponent id={parentId} onSelect={select} />
            </Box>
            <ShareCommentsTabRegistrar />
            <RequestedInformationTabRegistrar />
        </Bound>
    )

    function select(item) {
        if (item.children || item.hasChildren) {
            const current = new URLSearchParams(window.location.search)
            setSearchParams(clean({ ...Object.fromEntries(current.entries()), id: item.id }))
        }
    }

    function open(schedule) {
        if (handleUnlicensedContent(schedule)) return
        navigate(scheduleProfileRoute({ stats: { id: schedule.id }, schedule }))
    }

    // The Overview (maintenance profile) table reads these from context; without them its rows
    // default to /app/schedules routes that share recipients cannot reach
    function scheduleProfileRoute({ stats, schedule }) {
        return getFacilityProfileScheduleRoute({ stats, schedule, share })
    }

    function regimeNavigatorRoute(item) {
        return getFacilityProfileNavigatorRoute({ item, share })
    }
}
